/**
 * K108-R Batch 2 — DEV-ONLY monitored-evidence debug readout.
 *
 * Renders NOTHING unless localStorage `training_debug === 'true'`. Purely for
 * live-testing the aggregation read model (Batch 2) before Batch 3 surfaces it
 * in the completion modal. Shows engagement evidence only — clearly labelled as
 * NOT verified completion. Never blocks a launch; degrades to "(no evidence)".
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTrainingSessionMonitoringSummary } from '@/hooks/useTrainingSessionMonitoringSummary';

function isTrainingDebug(): boolean {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('training_debug') === 'true';
  } catch {
    return false;
  }
}

export function TrainingMonitoringDebugPanel({
  technicianProfileId,
}: {
  technicianProfileId?: string | null;
}) {
  const enabled = isTrainingDebug() && !!technicianProfileId;

  const { data: recent } = useQuery({
    queryKey: ['training-monitoring-debug-recent', technicianProfileId],
    enabled,
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('training_consumption_sessions')
        .select('id, artifact_id, created_at')
        .eq('technician_profile_id', technicianProfileId as string)
        .order('created_at', { ascending: false })
        .limit(5);
      return (data ?? []) as { id: string; artifact_id: string | null; created_at: string }[];
    },
  });

  const sessionIds = (recent ?? []).map((r) => r.id);
  const { data: summaries } = useTrainingSessionMonitoringSummary({ sessionIds, enabled });

  if (!enabled) return null;

  const rows = Array.from(summaries?.values() ?? []);
  return (
    <div
      data-testid="training-monitoring-debug"
      className="mt-3 rounded-md border border-dashed border-amber-400 bg-amber-50/50 p-2 text-[11px] font-mono text-amber-900"
    >
      <div className="font-semibold">DEV · monitored evidence (engagement — NOT verified completion)</div>
      {rows.length === 0 ? (
        <div>(no recent monitored sessions)</div>
      ) : (
        rows.map((s) => (
          <div key={s.sessionId}>
            {s.sessionId.slice(0, 8)}:{' '}
            {s.evidenceUnavailable
              ? 'no evidence'
              : `engagement ${s.monitoredPercent ?? (s.observedMinutes != null ? `~${s.observedMinutes}min` : '—')}` +
                (s.monitoredPercent != null ? '%' : '') +
                ` (active ${s.activeSeconds ?? '—'}s / dur ${s.videoDurationSec ?? '—'}s; reached ${s.maxVideoProgressPercent ?? '—'}%)` +
                ` conf=${s.confidence ?? '—'}` +
                `${s.resourceMismatch ? ' mismatch' : ''}${s.paused ? ' paused' : ''}${s.stopped ? ' stopped' : ''}`}
          </div>
        ))
      )}
    </div>
  );
}
